
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { blogService } from "@/services/blog.service"
import { Blog } from "@/types/api"
import { Button } from "@/components/ui/button"
import { Edit2, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { Badge } from "@/components/ui/badge"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function BlogsPage() {
    const [blogs, setBlogs] = useState<Blog[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [blogToDelete, setBlogToDelete] = useState<Blog | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const { toast } = useToast()
    const navigate = useNavigate()
    const PAGE_SIZE = 10

    const fetchBlogs = async () => {
        setIsLoading(true)
        try {
            const response = await blogService.getBlogs({
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
            })
            setBlogs(response.results)
            setTotalCount(response.count)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить блоги",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchBlogs()
    }, [page])

    const handleDelete = async () => {
        if (!blogToDelete) return
        
        setIsDeleting(true)
        try {
            await blogService.deleteBlog(blogToDelete.slug)
            toast({
                title: "Успешно",
                description: "Блог успешно удален",
            })
            setBlogToDelete(null)
            fetchBlogs()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось удалить блог",
                variant: "destructive",
            })
        } finally {
            setIsDeleting(false)
        }
    }

    const columns: Column<Blog>[] = [
        {
            header: "Название",
            accessorKey: "title",
            cell: (blog) => (
                <div>
                    <div className="font-medium">{blog.title}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{blog.slug}</div>
                </div>
            )
        },
        {
            header: "Тема",
            cell: (blog) => (
                <div>
                    {blog.theme ? (
                        <Badge variant="outline">{blog.theme.title}</Badge>
                    ) : (
                        <span className="text-xs text-gray-400">Нет темы</span>
                    )}
                </div>
            )
        },
        {
            header: "Автор",
            cell: (blog) => (
                <div className="text-sm">
                    {blog.author_name || blog.author?.full_name || <span className="text-gray-400">Нет автора</span>}
                </div>
            )
        },
        {
            header: "Ссылка",
            cell: (blog) => (
                <a
                    href={blog.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                    <ExternalLink className="h-3 w-3" />
                    Просмотр
                </a>
            )
        },
        {
            header: "Создано",
            cell: (blog) => (
                <div className="text-sm text-gray-600">
                    {new Date(blog.created_at).toLocaleDateString()}
                </div>
            )
        },
        {
            header: "Действия",
            className: "text-right",
            cell: (blog) => (
                <div className="flex justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/blogs/${blog.slug}/edit`)}
                    >
                        <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setBlogToDelete(blog)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Блоги</h1>
                    <p className="text-gray-600 mt-1">Управление блогами и статьями.</p>
                </div>
                <Button onClick={() => navigate("/blogs/new")}>
                    <Plus className="mr-2 h-4 w-4" />
                    Создать блог
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Список блогов</CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={blogs}
                        isLoading={isLoading}
                        pageCount={Math.ceil(totalCount / PAGE_SIZE)}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                </CardContent>
            </Card>

            <AlertDialog open={!!blogToDelete} onOpenChange={(open) => !open && setBlogToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Это навсегда удалит блог "{blogToDelete?.title}". Это действие нельзя отменить.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Удаление...
                                </>
                            ) : (
                                "Удалить"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
